import {
    useCallback, useMemo,
} from 'react'
import {
    Location,
} from 'app/Apps/ContactManagement/Locations/Locations.types'
import useValidateItem from 'app/hooks/useValidateItem'
import useActionLinkFunction from 'app/hooks/useActionLinkFunction'

import SkyNetLink, {
    SizeLink, TypeLink,
} from 'app/shared-components/SkyNetLink'
import useContactManagementRoutes from 'app/SkyNetRoutes/hooks/useContactManagementRoutes'
import {
    generateSkyNetLink,
} from 'app/SkyNetRoutes/skyNetRoutes.utils'

import {
    LocationType,
} from 'app/enums/label.enums'
import useStyles from './LocationSelector.style'
import useLocationRequests from './hooks/useLocationRequests'
import useLocationSearch from './hooks/useLocationSearch'
import {
    getBasicInfoByLocation, getTooltipData,
} from './utils'
import {
    LocationLabelIconType,
    LocationSelectorProps,
    LocationSelectorDefaultProps,
} from './types/LocationSelector.types'

type BaseLocationPropTypes = LocationSelectorProps & {
    searchComponent: (props: Record<string, any>) => JSX.Element
}

const BaseLocationSelector = ({
    value,
    filterByCompanyId,
    filterLocationType,
    filterAddressCategory,
    filterAddressMainCategory,
    isShippingLocation,
    isApprovedSubContractor,
    onChange,
    name,
    title,
    disabled,
    isRequired,
    validate,
    className,
    classNames,
    'data-testid': dataTestId,
    allowedLocationTypes,
    searchComponent: Component,
    includeFilters,
    setErrors,
    actionLinkFunction,
}: BaseLocationPropTypes) => {
    const {
        classes,
    } = useStyles()
    const {
        Locations: LocationsRoutes,
    } = useContactManagementRoutes()

    const getLabelConfig = useCallback<(location: Location) => LocationLabelIconType>(
        getBasicInfoByLocation, [])

    const {
        location,
        labelData,
    } = useLocationRequests(value, getLabelConfig)

    const tooltipValue = useMemo((): JSX.Element => {
        return getTooltipData(location)
    }, [location])

    const loadOptions = useLocationSearch({
        allowedLocationTypes,
        filterByCompanyId,
        filterLocationType,
        filterAddressCategory,
        filterAddressMainCategory,
        isApprovedSubContractor,
        isShippingLocation,
        classes,
        includeFilters,
    })

    const {
        errors,
        validated,
    } = useValidateItem({
        validate, value: location, setFormError: setErrors, key: name,
    })

    const handleActionLinkFunction = useActionLinkFunction<Location>({
        actionLinkFunction, value: location,
    })

    if (disabled && value && location?.locationType === LocationType.ADDRESS) {
        const link = generateSkyNetLink({
            domainPath: LocationsRoutes.Overview.path,
            params: {
                key: value,
            },
        })

        return (
            <div
                className={className}
                data-testid={dataTestId}
            >
                <SkyNetLink
                    type={TypeLink.EXTERNAL}
                    size={SizeLink.S}
                    title={title}
                    link={link}
                    text={labelData?.label}
                />
            </div>
        )
    }
    return (
        <div
            className={className}
            data-testid={dataTestId}
        >
            <Component
                name={name}
                value={value}
                title={title}
                onChange={onChange}
                loadOptions={loadOptions}
                labelData={labelData}
                labelField="label"
                className={className}
                classNames={{
                    options: classes.options,
                    ...classNames,
                }}
                disabled={disabled}
                isRequired={isRequired}
                iconTooltip={tooltipValue}
                errors={errors}
                validated={validated}
                actionLinkFunction={handleActionLinkFunction}
            />
        </div>
    )
}

BaseLocationSelector.defaultProps = LocationSelectorDefaultProps

export default BaseLocationSelector
